import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { Subscription, firstValueFrom } from 'rxjs';

/**
 *  OKIRAT ELLENŐRZÉSE – NYILVÁNOS OLDAL (/ellenorzes, /ellenorzes/:azonosito), BELÉPÉS NÉLKÜL.
 *
 *  Nkt. 57. § (6a)–(6b): a digitális bizonyítvány közokirat, a díszpéldányon a tanulmányi rendszer
 *  egyedi azonosítója. A kiállított bizonyítvány PDF-jének láblécében (és QR-kódjában) ez az oldal
 *  szerepel; a munkáltató, a másik iskola vagy bárki megnézheti, hogy az okiratot valóban kiállították-e,
 *  ki állította ki, mikor, és hogy azóta nem vonták-e vissza. SZEMÉLYES ADATOT NEM MUTAT – azt az
 *  okirat maga tartalmazza (backend/okirat-ellenorzes.js).
 *
 *  A díszpéldány saját azonosítója (DP-…) a díszpéldány-ellenőrzőhöz tartozik – oda irányítunk.
 */
interface Allapot {
  talalt: boolean;
  azonosito?: string;
  tipus?: string;
  tipus_nev?: string;
  tanev?: string | null;
  intezmeny?: { nev: string | null; om: string | null };
  kiallitva?: string | null;
  alairt?: boolean;
  idobelyeg?: { van: boolean | null; utolso: string | null; felulhitelesitesek: number; kovetkezo_esedekesseg: string | null; rendben: boolean | null };
  visszavont?: boolean;
  visszavonas_oka?: 'ujabb_kiallitas' | 'ervenytelenitve' | null;
  ervenyes?: boolean;
  uzenet?: string;
}

@Component({
  selector: 'app-okirat-ellenorzes',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './okirat-ellenorzes.component.html',
  styleUrls: ['./okirat-ellenorzes.component.scss'],
})
export class OkiratEllenorzesComponent implements OnInit, OnDestroy {
  azonosito = '';
  eredmeny: Allapot | null = null;
  hiba = '';
  fut = false;
  private feliratkozas?: Subscription;

  constructor(private http: HttpClient, private route: ActivatedRoute, private router: Router) {}

  ngOnInit() {
    this.feliratkozas = this.route.paramMap.subscribe((p) => {
      const az = String(p.get('azonosito') || '').trim();
      this.azonosito = az;
      this.eredmeny = null;
      this.hiba = '';
      if (az) this.lekerdez(az);
    });
  }

  ngOnDestroy() { this.feliratkozas?.unsubscribe(); }

  /** Díszpéldány-azonosító (DP-XXXX-XXXX-XXXXX) – annak saját ellenőrzője van. */
  get diszpeldanyKod(): boolean { return /^\s*DP[-\s]/i.test(this.azonosito); }

  get diszpeldanyUrl(): string {
    return `/api/diszpeldany/ellenorzes/${encodeURIComponent(this.azonosito.trim())}`;
  }

  ellenoriz() {
    const az = this.azonosito.trim();
    if (!az || this.diszpeldanyKod) return;
    //  A címbe írjuk: így a link megosztható, és a vissza gomb is működik.
    this.router.navigate(['/ellenorzes', az]);
  }

  private async lekerdez(az: string) {
    if (this.diszpeldanyKod) return;
    this.fut = true;
    try {
      //  Token nélkül: a végpont nyilvános, a bejelentkezett felhasználó jegye se menjen ki feleslegesen.
      this.eredmeny = await firstValueFrom(this.http.get<Allapot>(`/api/okirat-ellenorzes/${encodeURIComponent(az)}`,
        { headers: new HttpHeaders({ 'Skip-Auth': '1' }) }));
    } catch (e: any) {
      if (e?.status === 404) this.eredmeny = { talalt: false, uzenet: e?.error?.uzenet };
      else if (e?.status === 429) this.hiba = e?.error?.error || 'Túl sok kérés – próbáld újra egy perc múlva.';
      else this.hiba = 'Az ellenőrzés most nem érhető el. Próbáld újra később.';
    } finally {
      this.fut = false;
    }
  }

  /** 'YYYY-MM-DD' → 'YYYY. MM. DD.' */
  datum(d: string | null | undefined): string {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(d || '')) ? `${d!.slice(0, 4)}. ${d!.slice(5, 7)}. ${d!.slice(8, 10)}.` : '–';
  }

  get allapotSzoveg(): string {
    const e = this.eredmeny;
    if (!e?.talalt) return '';
    if (e.visszavont) return e.visszavonas_oka === 'ujabb_kiallitas' ? 'Visszavonva – újabb kiállítás váltotta ki' : 'Visszavonva (érvénytelenítve)';
    return e.ervenyes ? 'Érvényes, kiállított okirat' : 'Nyilvántartott okirat, aláírás nélkül';
  }

  get allapotSzin(): string {
    const e = this.eredmeny;
    if (!e?.talalt) return 'medium';
    if (e.visszavont) return 'danger';
    return e.ervenyes ? 'success' : 'warning';
  }

  get idobelyegSzoveg(): string {
    const b = this.eredmeny?.idobelyeg;
    if (!b || b.van === null) return 'Most nem állapítható meg';
    if (!b.van) return 'Nincs időbélyeg';
    return `Van – utolsó: ${this.datum(b.utolso)}`;
  }

  get felulhitelesitesSzoveg(): string {
    const b = this.eredmeny?.idobelyeg;
    if (!b) return '–';
    const db = b.felulhitelesitesek ? `${b.felulhitelesitesek} alkalommal felülhitelesítve` : 'Még nem volt esedékes';
    if (this.eredmeny?.visszavont) return db;
    if (b.rendben === false) return `${db} – a felülhitelesítés figyelmet igényel (a kiállító intézmény intézi)`;
    return b.kovetkezo_esedekesseg ? `${db}; a következő esedékes: ${this.datum(b.kovetkezo_esedekesseg)}` : db;
  }
}
